import { isWithinInterval } from "date-fns";
import { all, call, select } from "redux-saga/effects";

import { TripStatus, TripType } from "@mapmycustomers/shared";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import formatISODate from "@mapmycustomers/shared/util/formatters/formatISODate";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import FieldTimeCardData from "@app/scene/dashboard/store/cards/fieldTime/FieldTimeCardData";
import { callApi } from "@app/store/api/callApi";
import summarizeFieldTime, {
  FieldTimeSummary,
} from "@app/store/dashboard/fieldTime/summarizeFieldTime";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import FieldTimeCardConfiguration from "@app/types/dashboard/cards/fieldTime/FieldTimeCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import FaceTime from "@app/types/FaceTime";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { getLocalTimeZone } from "@app/util/dates";
import { parseApiDate, parseApiDateWithUtcTz } from "@app/util/parsers";

// day as epoch millis + drive total
interface DriveDayBucket {
  key: number;
  sum_duration?: { value?: number };
}

// zone-less day + sum (seconds)
interface FaceDayBucket {
  day: string;
  sum?: number;
}

// completed business trips, start OR end in range (mirrors driveTime.ts), scoped to userIds
export const buildFieldTimeTripFilters = (
  range: DateRange,
  userIds: User["id"][],
  isOwnUserScope: boolean
) => ({
  status: TripStatus.COMPLETED,
  ...(isOwnUserScope ? {} : { type: TripType.BUSINESS }),
  $or: [
    { startTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
    { endTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
  ],
  userId: userIds.length ? { [PlatformFilterOperator.CONTAINS]: userIds } : undefined,
});

// sums totalFaceTime with includeUnverified so trip-gap + unverified check-in time is
// counted (the BE zeroes it otherwise); `date` is zone-less, so edges are date-only strings
export const buildFieldTimeFaceFilters = (range: DateRange, userIds: User["id"][]) => ({
  $and: [
    ...(userIds.length ? [{ userId: { $in: userIds } }] : []),
    { date: { $gte: formatISODate(range.startDate), $lte: formatISODate(range.endDate) } },
  ],
  includeUnverified: true,
});

// split combined-window day-buckets into current/previous by date; out-of-window dropped
export const splitFieldTimeWindows = (
  tripBuckets: DriveDayBucket[],
  faceBuckets: FaceDayBucket[],
  currentRange: DateRange,
  previousRange: DateRange
): { current: FieldTimeSummary; previous: FieldTimeSummary } => {
  // both ranges' edges are local day boundaries; compare bucket instants against them directly
  const classify = (day: Date): "current" | "previous" | undefined => {
    if (isWithinInterval(day, { end: currentRange.endDate, start: currentRange.startDate })) {
      return "current";
    }
    if (isWithinInterval(day, { end: previousRange.endDate, start: previousRange.startDate })) {
      return "previous";
    }
    return undefined;
  };

  const currentDrive: number[] = [];
  const previousDrive: number[] = [];
  tripBuckets.forEach((bucket) => {
    // day-histogram key is the local-day start instant
    const period = classify(parseApiDate(bucket.key));
    if (period) {
      (period === "current" ? currentDrive : previousDrive).push(bucket.sum_duration?.value ?? 0);
    }
  });

  const faceTime = { current: 0, previous: 0 };
  faceBuckets.forEach((bucket) => {
    // `date` is a zone-less calendar date; read it in UTC (like Deal.closingDate) so the day
    // isn't shifted by the local offset — yields the local-midnight instant to compare
    const period = classify(parseApiDateWithUtcTz(bucket.day));
    if (period) {
      faceTime[period] += bucket.sum ?? 0;
    }
  });

  return {
    current: summarizeFieldTime({ driveMinutesByDay: currentDrive, faceSeconds: faceTime.current }),
    previous: summarizeFieldTime({
      driveMinutesByDay: previousDrive,
      faceSeconds: faceTime.previous,
    }),
  };
};

export function* fetchFieldTimeCardDataHelper(params: {
  callback: (data: FieldTimeCardData) => void;
  configuration: FieldTimeCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { dateRange } = configuration;

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const isUserScope = !scope?.teamId && !!scope?.userId;
  const currentUserId: User["id"] = yield select(getCurrentUserId);
  const isOwnUserScope = isUserScope && currentUserId === scope?.userId;

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  let userIds = scope?.userId ? [scope.userId] : [];
  if (isTeamScope) {
    userIds = teams.find(({ id }) => id === scope?.teamId)?.users.map(({ id }) => id) ?? [];
  }

  // both periods in one request per entity, then split — 2 calls, not 4
  const currentRange = getDatesRange(dateRange.range, dateRange.subRange);
  const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
  const combinedRange: DateRange = {
    endDate: currentRange.endDate,
    startDate: previousRange.startDate,
  };

  const tripPayload = {
    $aggs: {
      primaryGroup: { field: "startTime", interval: "day", timezone: getLocalTimeZone() },
      sum: ["duration"],
    },
    $filters: buildFieldTimeTripFilters(combinedRange, userIds, isOwnUserScope),
    $limit: 0,
  };
  const facePayload = {
    $aggs: {
      primaryGroup: { field: "date", interval: "day" },
      sum: ["totalFaceTime"],
    },
    $filters: buildFieldTimeFaceFilters(combinedRange, userIds),
    $limit: 0,
  };

  const [tripResponse, faceResponse]: [
    AggregatedListResponse<Trip, DriveDayBucket[]>,
    AggregatedListResponse<FaceTime, FaceDayBucket[]>,
  ] = yield all([
    callApi("fetchTrips", orgId, tripPayload),
    callApi("fetchFaceTime", orgId, facePayload),
  ]);

  const { current, previous } = splitFieldTimeWindows(
    tripResponse.aggregations ?? [],
    faceResponse.aggregations ?? [],
    currentRange,
    previousRange
  );

  yield call(callback, { ...current, previous });
}
